import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { formatCurrency } from '@/utils/format';
import { Plane } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function AirportTransferSelector({ transferData, onTransferChange }) {
  const [showTransferOptions, setShowTransferOptions] = useState(false);
  const [prices, setPrices] = useState({
    one_way: 50
  });
  const [transferSettings, setTransferSettings] = useState({
    enable_transfer_arrival: true,
    transfer_arrival_message: "For your departure please attend the reservation 1 day before"
  });

  // Fetch transfer prices and settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/transfer-prices`);
        setPrices({
          one_way: response.data.transfer_price_one_way || 50
        });
        setTransferSettings({
          enable_transfer_arrival: response.data.enable_transfer_arrival ?? true,
          transfer_arrival_message: response.data.transfer_arrival_message || "For your departure please attend the reservation 1 day before"
        });
      } catch (error) {
        console.error('Failed to fetch transfer settings:', error);
        // Use defaults on error
      }
    };
    fetchSettings();
  }, []);

  const handleRemoveTransfer = () => {
    onTransferChange({
      required: false,
      type: '',
      arrival_flight_number: '',
      arrival_flight_time: '',
      departure_flight_number: '',
      departure_flight_time: ''
    });
    setShowTransferOptions(false);
  };

  const getTransferPrice = () => {
    if (!transferData.type) return 0;
    return prices.one_way;
  };

  // Check if arrival transfer is enabled
  const anyTransferEnabled = transferSettings.enable_transfer_arrival;

  // Don't show anything if all transfer types are disabled
  if (!anyTransferEnabled) {
    return null;
  }

  if (!showTransferOptions && !transferData.required) {
    // Determine default transfer type based on what's enabled
    const getDefaultType = () => {
      if (transferSettings.enable_transfer_arrival) return 'arrival';
      return '';
    };

    return (
      <Card className="mt-4 border-2 border-dashed border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 hover:border-blue-500 hover:shadow-lg transition-all">
        <CardContent className="p-6">
          <button
            type="button"
            onClick={() => {
              setShowTransferOptions(true);
              onTransferChange({ ...transferData, required: true, type: getDefaultType() });
            }}
            className="w-full flex flex-col items-center gap-3 group"
          >
            <div className="p-4 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
              <Plane className="w-8 h-8 text-blue-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Add Airport Transfer</h3>
              <p className="text-sm text-gray-600">From {formatCurrency(prices.one_way)} one-way</p>
            </div>
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4 border-2 border-blue-500 bg-white shadow-md">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-full">
              <Plane className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold">Airport Transfer</h3>
          </div>
          <button
            type="button"
            onClick={handleRemoveTransfer}
            className="text-red-600 hover:text-red-800 text-sm font-medium"
          >
            Remove
          </button>
        </div>

        <div className="space-y-4">
          {/* Transfer Type */}
          <div>
            <Label className="text-sm font-medium">Transfer Type *</Label>
            <select
              value={transferData.type}
              onChange={(e) => onTransferChange({ ...transferData, type: e.target.value })}
              className="w-full mt-1 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select transfer type</option>
              {transferSettings.enable_transfer_arrival && (
                <option value="arrival">Arrival Transfer - {formatCurrency(prices.one_way)}</option>
              )}
            </select>
          </div>

          {/* Message for Arrival Only */}
          {transferData.type === 'arrival' && transferSettings.transfer_arrival_message && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-amber-600 text-lg">ℹ️</span>
                <p className="text-sm text-amber-800 font-medium">
                  {transferSettings.transfer_arrival_message}
                </p>
              </div>
            </div>
          )}

          {/* Arrival Details */}
          {transferData.type === 'arrival' && (
            <div className="bg-blue-50 p-4 rounded-lg space-y-3 border border-blue-200">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Plane className="w-4 h-4" />
                Arrival Flight Details
              </h4>
              <div>
                <Label htmlFor="arrival_flight_number" className="text-sm">
                  Flight Number <span className="text-gray-500 text-xs">(optional)</span>
                </Label>
                <Input
                  id="arrival_flight_number"
                  value={transferData.arrival_flight_number}
                  onChange={(e) => onTransferChange({ ...transferData, arrival_flight_number: e.target.value })}
                  placeholder="e.g., BA123 or 'I don't know yet'"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="arrival_flight_time" className="text-sm">
                  Arrival Time <span className="text-gray-500 text-xs">(optional)</span>
                </Label>
                <Input
                  id="arrival_flight_time"
                  type="time"
                  value={transferData.arrival_flight_time}
                  onChange={(e) => onTransferChange({ ...transferData, arrival_flight_time: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty if you don't know yet</p>
              </div>
            </div>
          )}

          {/* Transfer Total */}
          {transferData.type && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-700">Transfer Total:</span>
                <span className="text-xl font-bold text-blue-600">
                  {formatCurrency(getTransferPrice())}
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default AirportTransferSelector;
